import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import { usePusherChannelManager } from "src/hooks/use-pusher-channel-manager";

import { JobRunsListContainerQuery } from "./__generated__/JobRunsListContainerQuery.graphql";
import DigestJobRunList from "./components/DigestJobRunList";

// Define refetchable fragment for job runs data
const jobRunsListContainerFragment = graphql`
  fragment JobRunsListContainer_job on GraphQLJob
  @refetchable(queryName: "JobRunsListContainerRefetchQuery") {
    ...DigestJobRunList_job
    id
    name
    jobRuns(limit: 20, sortBy: "started_at", sortOrder: "desc") {
      edges {
        node {
          id
          status
          createdAt
          startedAt
          completedAt
          updatedAt
        }
      }
    }
  }
`;

const jobRunsListContainerQuery = graphql`
  query JobRunsListContainerQuery($jobId: GlobalID!) {
    node(id: $jobId) {
      ... on GraphQLJob {
        ...JobRunsListContainer_job
      }
    }
  }
`;

interface JobRunsListContainerProps {
  jobId: string;
}

export interface JobRunsListContainerRef {
  refresh: () => void;
}

const JobRunsListContainer = forwardRef<JobRunsListContainerRef, JobRunsListContainerProps>(({ jobId }, ref) => {
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const lastRefreshTime = useRef<number>(0);

  const data = useLazyLoadQuery<JobRunsListContainerQuery>(
    jobRunsListContainerQuery,
    { jobId },
    { fetchPolicy: "store-or-network" }
  );

  const job = data.node;

  // Use refetchable fragment to access job runs data
  const [jobWithRuns, refetch] = useRefetchableFragment(jobRunsListContainerFragment, job);

  const handleJobRunsRefresh = useCallback(() => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;

    // Use store-and-network for a less jarring experience
    // This tries cache first, then network, providing smoother updates
    refetch({}, { fetchPolicy: "store-and-network" });

    // Restore scroll position after a brief delay
    setTimeout(() => {
      if (scrollPositionRef.current > 0) {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: "auto" // Immediate, no smooth scrolling
        });
        scrollPositionRef.current = 0;
      }
    }, 10);
  }, [refetch]);

  useImperativeHandle(ref, () => ({
    refresh: handleJobRunsRefresh
  }));

  const { subscribe, unsubscribe } = usePusherChannelManager();

  // Get job runs that need monitoring for updates
  const allJobRuns = (jobWithRuns as any).jobRuns?.edges?.map((edge: any) => edge.node) || [];

  // Only monitor actively running job runs (PENDING or IN_PROGRESS)
  // Terminal states (COMPLETED, ERRORED, STOPPED) don't need monitoring
  const jobRunsToMonitor = allJobRuns.filter((jobRun: any) => {
    return jobRun.status === "PENDING" || jobRun.status === "IN_PROGRESS";
  });

  const jobRunIdsToMonitor = jobRunsToMonitor.map((jobRun: any) => jobRun.id);

  // Minimal debug logging (only log when monitoring changes)
  const monitoringKey = jobRunIdsToMonitor.sort().join(",");
  const prevMonitoringKey = React.useRef<string>("");

  if (monitoringKey !== prevMonitoringKey.current) {
    prevMonitoringKey.current = monitoringKey;
  }

  // Set up Pusher subscriptions for non-completed job runs
  useEffect(() => {
    const subscriptions: { channel: any; jobRunId: string }[] = [];

    const handleJobRunUpdate = (data: { id: string }) => {
      // Event contains the job run id that was updated
      // Only refresh if this job run is currently visible in our list
      const jobRunExists = allJobRuns.some((run: any) => run.id === data.id);

      if (jobRunExists) {
        // Find the current job run to check if this might be a completion event
        const currentJobRun = allJobRuns.find((run: any) => run.id === data.id);
        const wasInProgress = currentJobRun?.status === "IN_PROGRESS";

        // Clear any existing timeout to debounce rapid updates
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }

        // For potentially completion events (was in progress), refresh immediately
        // For other updates, use debouncing to avoid excessive refreshes
        const isLikelyCompletion = wasInProgress;
        const debounceTime = isLikelyCompletion ? 0 : 1000; // Longer debounce for non-completion events

        // Schedule refresh (immediate for completion events, debounced for others)
        refreshTimeoutRef.current = setTimeout(() => {
          lastRefreshTime.current = Date.now();
          handleJobRunsRefresh();
          refreshTimeoutRef.current = null;
        }, debounceTime);
      }
    };

    // Subscribe to actively running job runs (PENDING or IN_PROGRESS)
    jobRunIdsToMonitor.forEach((jobRunId: string) => {
      try {
        const channel = subscribe(jobRunId);
        if (channel) {
          channel.bind("JOB_RUN_UPDATED", handleJobRunUpdate);
          subscriptions.push({ channel, jobRunId });
        }
      } catch (error) {
        console.error("Subscription error for job run:", jobRunId, error);
      }
    });

    // Cleanup function
    return () => {
      // Clear any pending refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      subscriptions.forEach(({ channel, jobRunId }) => {
        try {
          channel.unbind("JOB_RUN_UPDATED", handleJobRunUpdate);
          unsubscribe(jobRunId);
        } catch {
          // Silently handle unsubscribe errors
        }
      });
    };
  }, [jobRunIdsToMonitor.join(","), subscribe, unsubscribe, handleJobRunsRefresh]);

  if (!job || !jobWithRuns) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Job not found
      </div>
    );
  }

  return <DigestJobRunList job={jobWithRuns as any} onRefresh={handleJobRunsRefresh} />;
});

JobRunsListContainer.displayName = "JobRunsListContainer";

export default JobRunsListContainer;